import React, { type ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import { SharingNewDropdown } from 'reactApp/components/SharingNewBiz/Dropdown/SharingNewDropdown';
import { EDropdownColors } from 'reactApp/components/SharingNewBiz/Dropdown/SharingNewDropdown.types';
import { TOOLTIP_OFFSET } from 'reactApp/components/SharingNewBiz/SharingNew.constants';
import { sendDwh, sendGa } from 'reactApp/components/SharingNewBiz/SharingNew.helpers';
import { type IAccessRightsOptions, EAccessRights } from 'reactApp/components/SharingNewBiz/SharingNew.types';
import { deleteUser, setAccessRight } from 'reactApp/modules/folderAccessControlList/folderAccessControlList.actions';
import { XrayItemType } from 'reactApp/modules/home/home.types';
import { unmountRequest } from 'reactApp/modules/modifying/modifying.actions';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { weblinkDeleteEmailAccess } from 'reactApp/modules/weblinkAccessControl/weblinkAccessControl.actions';
import { Dropdown } from 'reactApp/ui/Dropdown/Dropdown';
import { DropdownMenu } from 'reactApp/ui/Dropdown/DropdownMenu';
import { tooltipPlacements } from 'reactApp/ui/Tooltip/Tooltip.constants';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { DELETE_USER, getAccessRightsOptions, UNMOUNT_USER } from './User.constants';
import styles from './User.css';
import { renderDeleteUserDialog } from './User.helpers';

// eslint-disable-next-line max-lines-per-function, complexity
export const useAccessRights = ({
    user,
    item,
    isMounted,
    onClose,
    isPhone,
    isOwner,
    publicId,
    isPersonalAccessSharing,
}): ReactElement | null | string => {
    const dispatch = useDispatch();
    const folderId = !isPersonalAccessSharing ? item?.id : null;
    const weblink = !isPersonalAccessSharing ? null : publicId;
    const { isDomainAndEditRestricted } = getDomainRestrictionsForItems(item);
    const accessRights = user?.accessRights === 'read' || isDomainAndEditRestricted ? EAccessRights.READ_ONLY : user?.accessRights;
    const accessRightsOptions = getAccessRightsOptions();
    const userRight = accessRightsOptions[accessRights];
    const itemStorage = useSelector(getCurrentStorage);

    const onActionClick = useCallback(() => {
        if (!user || !item) {
            return;
        }

        let xrayItemType = XrayItemType.file;
        if (itemStorage === EStorageType.albums) {
            xrayItemType = XrayItemType.album;
        } else if (item.isFolder) {
            xrayItemType = XrayItemType.folder;
        }

        sendDwh({
            action: 'delete-user',
            dwhData: {
                type_object: item?.kind,
            },
        });

        sendPaymentGa({
            eventCategory: publicId ? ECategoryGa.public : ECategoryGa.sharedFolder,
            action: 'shutdown-sharing',
            public_id: publicId,
        });

        if (!isPersonalAccessSharing) {
            sendXray('sharing-biz-old-delete-user');

            dispatch(deleteUser({ email: user.email, item, name: user.name }));
        } else {
            sendXray(['sharing-biz-old-delete-weblink-access', xrayItemType]);

            dispatch(weblinkDeleteEmailAccess({ email: user.email, weblink }));
        }
    }, [user, item, itemStorage, publicId, isPersonalAccessSharing, dispatch, weblink]);

    const handleAccessRightsUpdate = useCallback(
        (accessRight: EAccessRights): void => {
            if (!user) {
                return;
            }

            if (accessRight === DELETE_USER.key) {
                renderDeleteUserDialog({ user, onActionClick, isFolder: item?.isFolder });
                return;
            }

            sendGa('user-rights-change');

            dispatch(
                setAccessRight({ email: user.email, accessRight, folderId, weblink, isFolder: item?.isFolder, isPersonalAccessSharing })
            );
        },
        [dispatch, onActionClick, user, folderId, weblink, item, isPersonalAccessSharing]
    );

    const onUnmount = useCallback(() => {
        sendGa('unmount');

        sendXray('sharing-biz-old-unmount');

        onClose();
    }, [onClose]);

    const handleOpenUnmountDialog = useCallback(() => {
        dispatch(unmountRequest({ item, onUnmount }));
    }, [item, onUnmount]);

    const renderAccessRightsDropdown = useCallback(
        (onClick: void): ReactElement | null => {
            const values = {
                ...accessRightsOptions,
                [EAccessRights.DELETE_USER]: DELETE_USER,
            };

            const list = Object.values(values)
                .filter((item: IAccessRightsOptions) => !(item.key === EAccessRights.READ_WRITE && isDomainAndEditRestricted))
                .map((item: IAccessRightsOptions) => ({
                    id: item.key,
                    text: item.text,
                    check: item.key === accessRights,
                    qaValue: item.key,
                    isDanger: item.key === EAccessRights.DELETE_USER,
                }));

            return <DropdownMenu list={list} theme="octavius" onClick={onClick} className={styles.dropdownMenu} />;
        },
        [accessRights, isDomainAndEditRestricted]
    );

    if (isOwner) {
        return <div className={styles.owner}>Владелец</div>;
    }

    if (isMounted) {
        return (
            <div className={styles.unmount} onClick={handleOpenUnmountDialog} data-qa-value={UNMOUNT_USER.key}>
                <div className={styles.unmountText}>{UNMOUNT_USER.text}</div>
            </div>
        );
    }

    return (
        <div className={styles.dropdown}>
            <Dropdown
                withinDialog
                theme="octavius"
                value={userRight?.text}
                qaValue={userRight?.key}
                as={SharingNewDropdown}
                color={isPhone ? EDropdownColors.BLUE : EDropdownColors.GRAY}
                placement={tooltipPlacements.BOTTOM_LEFT}
                content={renderAccessRightsDropdown}
                onClickItem={handleAccessRightsUpdate}
                tooltipOffset={TOOLTIP_OFFSET}
                animatedTooltip
                isPhone={isPhone}
                isUserDropdown
                tooltipClassName={isPhone ? styles.tooltip : undefined}
                noPageOffset
            />
        </div>
    );
};
